import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  ButtonBase,
  InputBase,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import {
  getFeatures,
  getRolePermissions,
  updateRolePermissions,
} from "../../../Api/RbacAdminApi";
import CODE from "../../../Static/Constants/StatusCodes";
import {
  FEATURE_CODES,
  WORK_ORDER_TYPE_FEATURE_PREFIX,
} from "../../../Static/FeatureMapping";
import { useToast } from "../../../Hooks/useToast";
import PermissionPill from "./PermissionPill";
import {
  OPERATION_LABELS,
  countFeatures,
  getToggleAllOps,
  isFeatureRowVisible,
  isToggleAllHeld,
  matchesSearch,
  rollupState,
  splitOperations,
  sumTotals,
  toggleAllForFeature,
} from "./permissionsMatrixHelpers";

// ORDER IS THE WIRE FORMAT. decodeBitmask below reads a permission integer by
// array index, so position N here IS bit N in every stored bitmask. New
// operations must be APPENDED. Inserting or reordering one silently shifts
// every permission already granted across every module.
export const ALL_OPERATIONS = [
  "view",
  "create",
  "update",
  "delete",
  "force_resolve",
  "approve",
  "critical_update",
];

// Feature codes that support force_resolve (backend may omit it from the features list).
const FORCE_RESOLVE_FEATURES = new Set([FEATURE_CODES.BMS_FAULTS_V2, FEATURE_CODES.PLATFORM_ALERTS_V2]);


// Work order types each get their own feature, WORK_ORDER.<TYPE>, so the set of
// codes supporting `approve` is open-ended and cannot be enumerated the way
// FORCE_RESOLVE_FEATURES is. Matched by prefix instead.
//
// Built from the exported prefix rather than retyped: the last time these two
// were separate string literals they drifted by one letter (WORK_ORDER vs
// WORK_ORDERS) and the Approve checkbox vanished with nothing on screen to say
// why -- the matrix hid it, and the backend guard built a code matching no
// feature row, which enforce() fails closed on.
const APPROVE_FEATURE_PREFIX = `${WORK_ORDER_TYPE_FEATURE_PREFIX}.`;

// Handles both bitmask (e.g. 3 = view+create) and array formats
export const decodeBitmask = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "number") {
    return ALL_OPERATIONS.filter((_, i) => value & (1 << i));
  }
  return [];
};

// Category may be a string or an object — extract a display label safely
const getCategoryLabel = (category) => {
  if (!category) return "General";
  if (typeof category === "string") return category;
  if (typeof category === "object") {
    return (
      category.categoryName ||
      category.name ||
      category.label ||
      category.categoryCode ||
      "General"
    );
  }
  return "General";
};

// API may return array [{category, features}] or object {categoryName: features[]}
const buildGrouped = (rawData) => {
  if (Array.isArray(rawData)) {
    const grouped = {};
    rawData.forEach((item) => {
      const label = getCategoryLabel(item.category);
      grouped[label] = Array.isArray(item.features) ? item.features : [];
    });
    return grouped;
  }
  return rawData || {};
};

// Category value may be an array or a keyed object — normalise to array
const toFeaturesArray = (features) => {
  if (Array.isArray(features)) return features;
  if (features && typeof features === "object") {
    return Object.entries(features).map(([code, f]) => ({
      featureCode: code,
      ...(typeof f === "object" ? f : {}),
    }));
  }
  return [];
};

// Returns the list of operations this feature supports.
// Falls back to all hierarchical ops (no force_resolve) when the API omits
// the field. For features in FORCE_RESOLVE_FEATURES, force_resolve is always
// included even if the backend omits it from the operations list.
export const getSupportedOps = (feature) => {
  // Arrays only: a string here (a mis-encoded DB value) would otherwise pass the
  // length check and break the .filter below.
  const nonEmptyList = (value) => (Array.isArray(value) && value.length > 0 ? value : null);
  const raw =
    nonEmptyList(feature.operations) ||
    nonEmptyList(feature.supportedOperations) ||
    nonEmptyList(feature.allowedOperations);

  const base = raw ? raw.filter((op) => ALL_OPERATIONS.includes(op)) : ["view", "create", "update", "delete"];

  // Ensure force_resolve is present for known features even if backend omits it
  if (FORCE_RESOLVE_FEATURES.has(feature.featureCode) && !base.includes("force_resolve")) {
    return [...base, "force_resolve"];
  }

  // Same for approve on a work order type. create and approve on one type may be
  // held by the same role -- that never lets anyone approve their own work order,
  // which workOrder enforces with a CHECK constraint and a service re-check.
  if (
    typeof feature.featureCode === "string" &&
    feature.featureCode.startsWith(APPROVE_FEATURE_PREFIX) &&
    !base.includes("approve")
  ) {
    return [...base, "approve"];
  }

  return base;
};

const INK = "#161726";
const INK_SOFT = "#54566F";
const INK_FAINT = "#8A8CA6";
const RULE = "#E5E6F1";
const RULE_SOFT = "#F0F1F7";
const HEAD_BG = "#EDEEF6";
const CAT_LINE = "#DDDFEC";
const HOVER_BG = "#F8F9FE";
const PRIMARY_PURPLE = "#4D44D8";

// Category rollup badge. Green when the role touches every feature in the
// category, dashed-empty when it touches none, plain when it is partway.
const ROLLUP_TONE = {
  full: { bg: "#E8F6ED", border: "1px solid #B7E0C5", color: "#15803D" },
  none: { bg: "transparent", border: `1px dashed ${RULE}`, color: INK_FAINT },
  partial: { bg: "#FCFCFE", border: `1px solid ${RULE}`, color: INK_SOFT },
};

const RolePermissionsMatrix = ({ roleId, roleName, saveTrigger }) => {
  const [featuresGroups, setFeaturesGroups] = useState({});
  const [permissions, setPermissions] = useState({});
  // featureCode → featureId (numeric) — needed for the save payload
  const [featureIdMap, setFeatureIdMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState("");
  const { openNotification } = useToast();

  useEffect(() => {
    if (!roleId) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId]);

  useEffect(() => {
    if (saveTrigger > 0) handleSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveTrigger]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [featuresRes, permissionsRes] = await Promise.all([
        getFeatures(),
        getRolePermissions(roleId),
      ]);

      const idMap = {};

      if (featuresRes?.responseStatus?.code === CODE.SUCCESS) {
        const grouped = buildGrouped(featuresRes.response?.data);
        setFeaturesGroups(grouped);

        // Build featureId map from features list
        Object.values(grouped).forEach((features) => {
          toFeaturesArray(features).forEach(({ featureCode, featureID, id }) => {
            if (featureCode && (featureID || id)) {
              idMap[featureCode] = featureID || id;
            }
          });
        });

        const init = {};
        Object.keys(grouped).forEach((k) => (init[k] = true));
        setExpanded(init);
      }

      if (permissionsRes?.responseStatus?.code === CODE.SUCCESS) {
        const rawData = permissionsRes.response?.data || {};
        const normalized = {};

        // Format A (actual API): { roleId, roleCode, permissions: [{featureID, featureCode, permissions: [...ops]}] }
        if (rawData.permissions && Array.isArray(rawData.permissions)) {
          rawData.permissions.forEach(
            ({ featureCode, featureID, permissions: ops, operations }) => {
              if (featureCode && featureID) idMap[featureCode] = featureID;
              const opsList = ops || operations;
              normalized[featureCode] = new Set(
                Array.isArray(opsList) ? opsList : decodeBitmask(opsList)
              );
            }
          );
        }
        // Format B: flat array [{featureCode, operations|permissions: [...]}]
        else if (Array.isArray(rawData)) {
          rawData.forEach(({ featureCode, featureID, operations, permissions: ops }) => {
            if (featureCode && featureID) idMap[featureCode] = featureID;
            const opsList = operations || ops;
            normalized[featureCode] = new Set(
              Array.isArray(opsList) ? opsList : decodeBitmask(opsList)
            );
          });
        }
        // Format C: object { featureCode: bitmask | ops[] }
        else {
          Object.entries(rawData).forEach(([code, ops]) => {
            if (typeof ops === "number" || Array.isArray(ops)) {
              normalized[code] = new Set(decodeBitmask(ops));
            }
          });
        }

        setPermissions(normalized);
      }

      setFeatureIdMap(idMap);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (featureCode, operation) => {
    setPermissions((prev) => {
      const current = new Set(prev[featureCode] || []);
      if (current.has(operation)) current.delete(operation);
      else current.add(operation);
      return { ...prev, [featureCode]: current };
    });
  };

  const handleToggleAll = (featureCode, supportedOps) => {
    setPermissions((prev) => ({
      ...prev,
      [featureCode]: toggleAllForFeature(prev[featureCode], supportedOps),
    }));
  };

  const handleSave = async () => {
    // Backend requires: { featureId (numeric), permissions: [...ops] }
    const payload = Object.entries(permissions)
      .filter(([featureCode, ops]) => featureIdMap[featureCode] != null && ops.size > 0)
      .map(([featureCode, ops]) => ({
        featureId: featureIdMap[featureCode],
        permissions: [...ops],
      }));

    const res = await updateRolePermissions(roleId, payload);
    if (res?.responseStatus?.code === CODE.SUCCESS) {
      openNotification({ text: "Permissions saved successfully" });
    }
  };

  const toggleCategory = (category) => {
    setExpanded((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  const setAllCategories = (open) => {
    setExpanded(
      Object.keys(featuresGroups).reduce((acc, k) => ({ ...acc, [k]: open }), {})
    );
  };

  // One pass over the payload builds every list and count the panel renders,
  // so the badges, the summary and the rows can never disagree about what is
  // granted -- they are reading the same computation.
  const categories = useMemo(
    () =>
      Object.entries(featuresGroups).map(([category, features]) => {
        const all = toFeaturesArray(features);
        const visible = all.filter((f) => matchesSearch(f, search));
        return {
          category,
          features: all,
          visibleFeatures: visible,
          totals: countFeatures(all, permissions, getSupportedOps),
        };
      }),
    [featuresGroups, permissions, search]
  );

  const totals = useMemo(
    () => sumTotals(categories.map((c) => c.totals)),
    [categories]
  );

  if (!roleId) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <Typography color="text.secondary">
          Select a role to view its permissions
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const searching = search.trim().length > 0;
  const collapseHint = searching
    ? "Clear the search to collapse categories"
    : "";
  const anyVisible = categories.some((c) => c.visibleFeatures.length > 0);

  return (
    <Box>
      {/* Panel header — title and live summary left, search right */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: INK }}>
            Feature Permissions
          </Typography>
          {/* Three numbers, three questions: how much of the product this role
              touches, how much power it has inside it, and how many privileged
              grants it holds. Recomputed on every toggle -- a static count goes
              stale the moment somebody edits, and a stale summary is worse
              than none. */}
          <Box
            aria-live="polite"
            aria-atomic="true"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              mt: 0.5,
              fontSize: 13,
              color: INK_FAINT,
              fontVariantNumeric: "tabular-nums",
              "& b": { color: INK_SOFT, fontWeight: 600 },
            }}
          >
            {roleName && (
              <>
                <span>{roleName}</span>
                <Box sx={{ width: "1px", height: 11, backgroundColor: RULE }} />
              </>
            )}
            <span>
              <b>{totals.featureGranted}</b> of <b>{totals.featureTotal}</b> features
            </span>
            <Box sx={{ width: "1px", height: 11, backgroundColor: RULE }} />
            <span>
              <b>{totals.permissionGranted}</b> of <b>{totals.permissionTotal}</b> permissions
            </span>
            <Box sx={{ width: "1px", height: 11, backgroundColor: RULE }} />
            <Box component="span" sx={{ "& b": { color: "#8A4B08" } }}>
              <b>{totals.capabilityGranted}</b> of <b>{totals.capabilityTotal}</b> capabilities
            </Box>
          </Box>
        </Box>

        <InputBase
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search feature or code..."
          inputProps={{ "aria-label": "Search features" }}
          data-testid="permission-search"
          startAdornment={
            <InputAdornment position="start" sx={{ ml: 0.5 }}>
              <SearchIcon sx={{ fontSize: 17, color: INK_FAINT }} />
            </InputAdornment>
          }
          sx={{
            fontSize: 13,
            minWidth: 240,
            px: 1,
            py: 0.75,
            border: `1px solid ${RULE}`,
            borderRadius: "9px",
            backgroundColor: "#fff",
            "&.Mui-focused": {
              outline: `2px solid ${PRIMARY_PURPLE}`,
              outlineOffset: "-1px",
              borderColor: "transparent",
            },
          }}
        />
      </Box>

      {categories.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography color="text.secondary">No features available</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                {/* Expand/collapse sit in the FEATURE column header: they act on
                    the chevrons directly below them, in the same column. */}
                <TableCell
                  sx={{
                    backgroundColor: HEAD_BG,
                    width: "38%",
                    padding: "6px 16px 6px 20px",
                    borderBottom: `2px solid ${CAT_LINE}`,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        color: INK_SOFT,
                      }}
                    >
                      Feature
                    </Typography>
                    <Box
                      sx={{
                        display: "inline-flex",
                        border: `1px solid ${CAT_LINE}`,
                        borderRadius: "7px",
                        overflow: "hidden",
                        backgroundColor: "#fff",
                        "& .MuiIconButton-root": {
                          borderRadius: 0,
                          padding: "2px 6px",
                          color: INK_SOFT,
                          "&:hover": {
                            backgroundColor: "#EFEEFD",
                            color: PRIMARY_PURPLE,
                          },
                        },
                        "& .MuiIconButton-root:first-of-type": {
                          borderRight: `1px solid ${CAT_LINE}`,
                        },
                      }}
                    >
                      {/* Disabled while searching: a search shows every match
                          regardless of collapse, so these would be dead
                          controls. Disabling says that; leaving them live
                          would let the admin press collapse and watch
                          nothing happen. The collapse state itself is kept,
                          and comes back when the search is cleared. */}
                      <Tooltip title={collapseHint || "Expand all categories"}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={searching}
                            onClick={() => setAllCategories(true)}
                            aria-label="Expand all categories"
                            data-testid="expand-all-categories"
                          >
                            <KeyboardDoubleArrowDownIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={collapseHint || "Collapse all categories"}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={searching}
                            onClick={() => setAllCategories(false)}
                            aria-label="Collapse all categories"
                            data-testid="collapse-all-categories"
                          >
                            <KeyboardDoubleArrowUpIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: HEAD_BG,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: INK_SOFT,
                    padding: "11px 20px",
                    borderBottom: `2px solid ${CAT_LINE}`,
                    borderLeft: `1px solid ${CAT_LINE}`,
                  }}
                >
                  Permissions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!anyVisible && (
                <TableRow>
                  <TableCell colSpan={2} sx={{ textAlign: "center", py: 4, color: INK_FAINT }}>
                    {searching
                      ? "No feature matches that search."
                      : "No features available"}
                  </TableCell>
                </TableRow>
              )}

              {categories.flatMap(({ category, features, visibleFeatures, totals: catTotals }) => {
                if (visibleFeatures.length === 0) return [];

                // A search shows its matches wherever they are, so the
                // category reads as open while one is running. The chevron and
                // aria-expanded follow THIS value, not the stored one -- rows
                // visible under a closed chevron is the bug that makes a
                // filtered tree untrustworthy.
                const isExpanded = searching || expanded[category] !== false;
                const tone = ROLLUP_TONE[rollupState(catTotals)];
                const slug = category.replace(/\s+/g, "-").toLowerCase();

                const rows = [
                  <TableRow
                    key={`cat-${category}`}
                    sx={{ backgroundColor: "#fff", "&:hover": { backgroundColor: HOVER_BG } }}
                  >
                    <TableCell
                      colSpan={2}
                      sx={{
                        padding: 0,
                        borderBottom: `1px solid ${RULE}`,
                        borderTop: `1px solid ${CAT_LINE}`,
                      }}
                    >
                      {/* A real button, not a click handler on the <tr>: a row
                          cannot be tabbed to, and aria-expanded on a bare <tr>
                          advertises an affordance the keyboard cannot reach. */}
                      <ButtonBase
                        type="button"
                        onClick={() => toggleCategory(category)}
                        disabled={searching}
                        aria-expanded={isExpanded}
                        aria-label={`${category}, ${catTotals.featureGranted} of ${catTotals.featureTotal} features granted`}
                        data-testid={`category-row-${slug}`}
                        disableRipple
                        sx={{
                          width: "100%",
                          justifyContent: "flex-start",
                          padding: "12px 20px",
                          textAlign: "left",
                          "&.Mui-disabled": { opacity: 1 },
                          "&:focus-visible": {
                            outline: `2px solid ${PRIMARY_PURPLE}`,
                            outlineOffset: "-2px",
                          },
                        }}
                      >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, width: "100%" }}>
                        {isExpanded ? (
                          <KeyboardArrowDownIcon sx={{ fontSize: 17, color: INK_FAINT }} />
                        ) : (
                          <KeyboardArrowRightIcon sx={{ fontSize: 17, color: INK_FAINT }} />
                        )}
                        <Typography sx={{ fontSize: 14, fontWeight: 650, color: INK }}>
                          {category}
                        </Typography>
                        {/* No standalone feature count: the badge already
                            carries the denominator, and a bare number beside
                            "4 / 12 granted" does not say which is which. */}
                        <Box
                          sx={{
                            ml: "auto",
                            fontSize: 12,
                            fontWeight: 600,
                            px: 1.25,
                            py: 0.25,
                            borderRadius: 999,
                            fontVariantNumeric: "tabular-nums",
                            ...tone,
                          }}
                        >
                          {catTotals.featureGranted} / {catTotals.featureTotal} granted
                        </Box>
                      </Box>
                      </ButtonBase>
                    </TableCell>
                  </TableRow>,
                ];

                features.forEach((feature) => {
                  const matches = matchesSearch(feature, search);
                  if (!isFeatureRowVisible({ matches, expanded: isExpanded, searching })) {
                    return;
                  }

                  const supportedOps = getSupportedOps(feature);
                  const { core, capabilities } = splitOperations(supportedOps);
                  const held = permissions[feature.featureCode];
                  const featureLabel = feature.featureName || feature.featureCode;
                  // "Select all" skips critical_update, so neither the label
                  // nor the disabled state may count it.
                  const toggleAllOps = getToggleAllOps(supportedOps);
                  const allHeld = isToggleAllHeld(held, supportedOps);

                  rows.push(
                    <TableRow
                      key={`feat-${feature.featureCode}`}
                      data-testid={`feature-row-${feature.featureCode}`}
                      sx={{
                        "&:hover": { backgroundColor: HOVER_BG },
                        "&:last-child td": { borderBottom: 0 },
                      }}
                    >
                      <TableCell
                        sx={{
                          // Indent: the feature sits under its category.
                          padding: "10px 16px 10px 43px",
                          borderBottom: `1px solid ${RULE_SOFT}`,
                          verticalAlign: "middle",
                        }}
                      >
                        <Typography sx={{ fontSize: 13.5, fontWeight: 550, color: INK }}>
                          {featureLabel}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "monospace",
                            fontSize: 10.5,
                            color: INK_FAINT,
                            mt: "2px",
                            wordBreak: "break-all",
                          }}
                        >
                          {feature.featureCode}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "8px 20px",
                          borderBottom: `1px solid ${RULE_SOFT}`,
                          borderLeft: `1px solid ${RULE_SOFT}`,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
                          {core.map((op) => (
                            <PermissionPill
                              key={op}
                              label={OPERATION_LABELS[op]}
                              featureName={featureLabel}
                              checked={!!held?.has(op)}
                              onToggle={() => handleToggle(feature.featureCode, op)}
                              testId={`pill-${feature.featureCode}-${op}`}
                            />
                          ))}

                          {capabilities.length > 0 && (
                            <Box
                              sx={{
                                width: "1px",
                                alignSelf: "stretch",
                                margin: "6px 11px 6px 5px",
                                backgroundColor: RULE,
                              }}
                            />
                          )}

                          {capabilities.map((op) => (
                            <PermissionPill
                              key={op}
                              capability
                              label={OPERATION_LABELS[op]}
                              featureName={featureLabel}
                              checked={!!held?.has(op)}
                              onToggle={() => handleToggle(feature.featureCode, op)}
                              testId={`pill-${feature.featureCode}-${op}`}
                            />
                          ))}

                          {/* Bulk action, not a grant. Styled as a text button so
                              it cannot be mistaken for a seventh permission --
                              which is exactly how an "All" pill in this row
                              would read. */}
                          <Button
                            size="small"
                            disabled={toggleAllOps.length === 0}
                            onClick={() =>
                              handleToggleAll(feature.featureCode, supportedOps)
                            }
                            data-testid={`toggle-all-${feature.featureCode}`}
                            sx={{
                              ml: 0.5,
                              fontSize: 12,
                              fontWeight: 600,
                              textTransform: "none",
                              color: INK_FAINT,
                              minWidth: 0,
                              px: 0.75,
                              "&:hover": { color: PRIMARY_PURPLE, backgroundColor: "transparent" },
                            }}
                          >
                            {allHeld ? "Clear all" : "Select all"}
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                });

                return rows;
              })}
            </TableBody>
          </Table>

          {/* Legend: the three pill states, named. Without it the amber pills
              read as a warning rather than as a different kind of grant. */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px 20px",
              flexWrap: "wrap",
              padding: "12px 20px",
              borderTop: `1px solid ${CAT_LINE}`,
              backgroundColor: HEAD_BG,
              fontSize: 12.5,
              color: INK_SOFT,
            }}
          >
            <PermissionPill decorative label="Granted" checked />
            <PermissionPill decorative label="Not granted" />
            <PermissionPill decorative label="Capability" capability checked />
            <span>
              Capabilities are feature-specific authority — they appear only where a
              feature declares one
            </span>
          </Box>
        </TableContainer>
      )}
    </Box>
  );
};

export default RolePermissionsMatrix;
